import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import api, { API_ENDPOINTS } from '../config/api';
import toast from 'react-hot-toast';
import { FOOTER_SOCIAL_LINKS } from './socialIcons';
import { FOOTER_LINK_GROUPS } from '../config/footerLinks';
import FooterLink from './FooterLink';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post(API_ENDPOINTS.EMAIL_SUBSCRIPTIONS.SUBSCRIBE, {
        email: email.trim(),
        source: 'blog'
      });
      toast.success('Successfully subscribed!');
      setEmail('');
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to subscribe. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialLinks = FOOTER_SOCIAL_LINKS;

  return (
    <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="mb-4">
              <h2 className="text-xl sm:text-2xl font-heading font-bold text-gradient mb-2">
                Workloob Blog
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                Discover insightful stories, expert opinions, and the latest trends in technology, business, and innovation.
              </p>
            </div>
            
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Stay updated
              </p>
              <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  disabled={isSubmitting}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg sm:rounded-l-lg sm:rounded-r-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-accent hover:bg-accent/90 text-white rounded-lg sm:rounded-l-none sm:rounded-r-lg transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Mail className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>

          {FOOTER_LINK_GROUPS.map((group) => (
            <div key={group.title}>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {group.title}
              </h3>
              <ul className="space-y-2">
                {group.links.map((link) => (
                  <li key={link.name}>
                    <FooterLink {...link} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 text-center sm:text-left">
              <div className="flex flex-wrap justify-center sm:justify-start items-center gap-1">
                <span>© {currentYear} Workloob Blog. All rights reserved.</span>
                <span className="hidden sm:inline">•</span>
                <Link to="/privacy" className="hover:text-accent transition-colors duration-200">Privacy</Link>
                <span>•</span>
                <Link to="/terms" className="hover:text-accent transition-colors duration-200">Terms of Use</Link>
                <span>•</span>
                <Link to="/guidelines" className="hover:text-accent transition-colors duration-200">Community Guidelines</Link>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Follow us:
              </p>
              <div className="flex items-center space-x-3">
                {socialLinks.map((social) => {
                  const IconComponent = social.icon;
                  return (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 dark:text-gray-400 hover:text-accent transition-colors duration-200"
                      aria-label={social.label}
                    >
                      <IconComponent className="w-4 h-4 sm:w-5 sm:h-5" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
